import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from 'notistack';
import { Box, Button, Dialog, DialogContentText, DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';

import FileDropBox from '../../filedropbox/FileDropBox';
import UploadIllusionEDLSummary from './UploadIllusionEDLSummary';
import UploadIllusionModifyDatagrid from './UploadIllusionModifyDatagrid';
import ProjectContext from '../../../contexts/ProjectContext';
import UserContext from '../../../contexts/UserContext';
import EDLSubmissionWarningDialog from '../EDLSubmissionWarningDialog';

import UploadIllusionEDLDropdowns from './UploadIllusionEDLDropdowns';
import {useFetch} from '../../../components/auth/useFetch';
import UploadIllusionEDLPullModSummary from './UploadIllusionEDLPullModSummary';

function UploadIllusionEDLDialog(props) {
    const { isQTUploading, open, qt, setQT, setOpen, uploadQTFile, vendors, pullTypes } = props;
    const { isAuthenticated } = useAuth0();
    const { enqueueSnackbar } = useSnackbar();
    const { konsolUser } = useContext(UserContext);
    const projectContext = useContext(ProjectContext);
    const qtFileInputRef = useRef(null);

    const [edl, setEDL] = useState(null);
    const [edlExists, setEdlExists] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitInProgress, setSubmitInProgress] = useState(false);

    const [edlSummary, setEdlSummary] = useState(null);

    const [editedPlates, setEditedPlates] = useState([]);
    const [modifiedPlates, setModifiedPlates] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [advancedSettings, setAdvancedSettings] = useState(false);
    const [EdlWarningDialogOpen, setEdlWarningDialogOpen] = useState(false);
    const [illusionSubmissionStatus, setIllusionSubmissionStatus] = useState('progress');

    const [vendor, setVendor] = useState(vendors?.at(0) ?? {});
    const [pullType, setPullType] = useState(pullTypes?.at(0) ?? {});
    const fetchWithAuth = useFetch();

    useEffect(() => {
        const selectedPlates = editedPlates.filter(plate => selectionModel.includes(plate.id))
        const noPlatesSelected = selectedPlates.length === 0
        const duplicatePlatesExist = getDuplicatePlates(selectedPlates).length > 0
        const isVendorEmpty = vendors.length > 0 && Object.keys(vendor).length === 0;
        const isPullTypeEmpty = pullTypes.length > 0 && Object.keys(pullType).length === 0;
        setSubmitDisabled(noPlatesSelected || duplicatePlatesExist || isVendorEmpty || isPullTypeEmpty);
    }, [editedPlates, selectionModel, vendor, vendors, pullType, pullTypes]);


    useEffect(() => {
        setModifiedPlates([
        ...(edlSummary?.plates ?? []),
        ...(edlSummary?.missing_plates ?? [])
        ]);
    }, [edlSummary]);

    useEffect(() => {
        isAuthenticated &&
            (async () => {
                try {
                    if (edl && ['.edl', '.csv', '.tab', '.txt'].some(ext =>  edl?.name?.toLowerCase()?.endsWith(ext))) {
                        var formdata = new FormData();
                        formdata.append('file_upload', edl);
                        var requestOptions = {
                            method: 'POST',
                            body: formdata,
                            redirect: 'follow',
                            headers: {
                                'X-Project-ID': projectContext?.project_db,
                            },
                        };
                        fetchWithAuth(
                            `${process.env.REACT_APP_BACKEND_URL}/konsol/illusionsummary/`,
                            requestOptions
                        )
                        .then((response) => response.json())
                        .then((res) => {
                            setEdlSummary(res);
                            setPlates(res);
                            setEdlExists(res.edl_exists);
                        });
                    }
                } catch (e) {
                    console.error(e);
                }
            })();
    }, [edl]);

    const getDuplicatePlates = (plates) => {
        let key = 'plate';
        if (pullType?.value?.startsWith('Drama')) {
            key = 'sequence'
        }
        const counts = plates.reduce((acc, obj) => {
            const value = obj?.[key] ?? '';
            acc[value] = (acc[value] || 0) + 1;
            return acc;
        }, {});
        return plates.filter(obj => counts[obj?.[key] ?? ''] > 1);
    };

    const onVendorChange = (e) => {
        const selected = vendors.find((v) => v.value === e.target.value);
        setVendor(selected);
    };

    const onPullTypeChange = (e) => {
        const selected = pullTypes.find((pt) => pt.value === e.target.value);
        setPullType(selected);
        setPlates(edlSummary);
    };

    const setPlates = (summary) => {
        setEditedPlates(summary?.plates ?? []);
        const submittedDuplicates = getDuplicatePlates(summary?.plates ?? [])
        const duplicatePlateIds = submittedDuplicates.map(plate => plate.id);
        setSelectionModel(summary?.plates?.filter(plate => !duplicatePlateIds.includes(plate.id)).map((plate) => plate.id) ?? []);
    }

    const handleClose = () => {
        setOpen(false);
        handleFormReset();
        setAdvancedSettings(false);
        setEDL(null);
        if (!isQTUploading) {
            setQT(null);
        }
    };

    const handleAdvancedClose = () => {
        setAdvancedSettings(false);
    }

    const handleFormReset = () => {
        setEdlExists(false);
        setSubmitDisabled(true);
        setEdlSummary(null);
        setEditedPlates([]);
        setIllusionSubmissionStatus('none');
        setSubmitInProgress(false);
        setVendor({});
        setPullType({});
    };

    const handleAdvancedSettings = () => {
        setAdvancedSettings(true);
    };

    const handleCancelAdvancedSettings = () => {
        setAdvancedSettings(false);
    };

    const handleEdlWarningDialogClose = () => {
        setSubmitDisabled(false);
        setEdlWarningDialogOpen(false);
    };

    const handleEdlWarningDialogOpen = () => {
        setEdlWarningDialogOpen(true);
    };

    const handleSubmit = () => {
        if (!edl) return;
        setSubmitDisabled(true);
        setSubmitInProgress(true);
        if (edlExists) {
            handleEdlWarningDialogOpen();
        } else {
            uploadEDL();
        }
    };

    const handleQTFileSelection = (event) => {
        let files = Array.from(event.target.files);

        if (files.length > 0) {
            setQT(files[0]);
        } else if (!isQTUploading && qt) {
            setQT(null);
        }
    };

    const uploadEDL = async () => {
        setIllusionSubmissionStatus('progress')
        var formdata = new FormData();
        formdata.append("file_upload", edl);
        formdata.append("vendor", vendor?.value ?? "");
        formdata.append("pull_type", pullType?.value ?? "");
        formdata.append('user', `${konsolUser?.given_name ?? ''} ${konsolUser?.family_name ?? ''}`);
        formdata.append("plates", JSON.stringify(editedPlates?.filter(plate => selectionModel.includes(plate.id)) ?? []));

        var requestOptions = {
            method: 'POST',
            body: formdata,
            redirect: 'follow',
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        };
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/illusionupload/`, requestOptions)
            .then(response => {
                if (response.ok){
                    setIllusionSubmissionStatus('success')
                    enqueueSnackbar("Submission successful", { variant: 'success' });
                    handleClose();
                    return
                } else {
                    setSubmitDisabled(false)
                    setIllusionSubmissionStatus('error')
                    enqueueSnackbar("Submission failed. Please try again.", { variant: 'error' });
                }
            })
            .then(async () => {
                if (qt && !isQTUploading) {
                    uploadQTFile();
                }
            })
            .catch((e) => {
                enqueueSnackbar("An unexpected error occurred.", { variant: 'error' });
                console.error(e);
            });
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={advancedSettings ? handleAdvancedClose : handleClose}
                maxWidth='lg'
                PaperProps={{
                    sx: {
                        backgroundColor: '#282828',
                        height: advancedSettings ? '100%' : 'auto',
                    },
                }}
            >
                <DialogTitle>
                    {advancedSettings ? 'Advanced Pull Modification Settings' : 'Upload Illusion EDL'}
                </DialogTitle>
                <DialogContentText
                    sx={{ color: 'rgb(175, 175, 175)', pl: 3, pr: 3 }}
                >
                    {advancedSettings ? 'Add or remove plates from the EDL by selecting the checkbox.' : ''}
                </DialogContentText>
                <DialogContent>
                    {!advancedSettings ? (
                        <React.Fragment>
                            <FileDropBox
                                titleText={'Drop Illusion EDL file here'}
                                UploadIcon={<CloudUploadIcon sx={{ fontSize: 48, opacity: 0.4 }} />}
                                setFile={setEDL}
                            />
                            <UploadIllusionEDLDropdowns
                                vendors={vendors}
                                vendor={vendor}
                                onVendorChange={onVendorChange}
                                pullTypes={pullTypes}
                                pullType={pullType}
                                onPullTypeChange={onPullTypeChange}
                            />
                            <Box
                                id='edl-summary'
                                sx={{
                                    backgroundColor: '#141414',
                                    borderRadius: '8px',
                                    minHeight: '75px',
                                    mt: 1, // margin-top: 2
                                    p: 0, // padding: 2
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'left',
                                    color: 'white',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {<LinearProgress sx={{ visibility: edl && !edlSummary ? 'visible' : 'hidden' }} />}
                                <Box
                                    id='edl-summary'
                                    sx={{
                                        backgroundColor: '#141414',
                                        borderRadius: '8px',
                                        mb: 0.5, // margin-top: 2
                                        padding: 3, // padding: 2
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'left',
                                        color: 'white',
                                        position: 'relative',
                                    }}
                                >
                                    {edl && edlSummary ? (
                                        <Box width='100%' display='flex' alignItems='center'>
                                            <Box>File: {edl.name}</Box>
                                        </Box>
                                    ) : (
                                        ''
                                    )}
                                    {edl && edlSummary ? (
                                        <Tooltip
                                            title={
                                                qt === null ? 'Attach QT file'
                                                : isQTUploading ? `${qt?.name ?? 'Another file'} is being uploaded`
                                                : `Unlink ${qt?.name ?? 'File'}`
                                            }
                                        >
                                            <IconButton
                                                disabled={isQTUploading}
                                                sx={{ display: 'flex', justifyContent: 'flex-end', color: 'white' }}
                                                onClick={() =>
                                                    qt === null
                                                        ? qtFileInputRef.current.click()
                                                        : setQT(null)
                                                }
                                            >
                                                {qt === null ? (
                                                    <AddLinkIcon />
                                                ) : (
                                                    <LinkIcon sx={{ color: 'orange' }}/>
                                                )}
                                            </IconButton>
                                            <input
                                                type='file'
                                                ref={qtFileInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handleQTFileSelection}
                                                accept='.mov,.mxf'
                                                multiple={false}
                                            />
                                        </Tooltip>
                                    ) : (
                                        ''
                                    )}
                                </Box>
                            </Box>
                            <UploadIllusionEDLSummary
                                selectionModel={selectionModel}
                                editedPlates={editedPlates}
                                getDuplicatePlates={getDuplicatePlates}
                                onAdvancedSettings={handleAdvancedSettings}
                                missingPlates={edlSummary?.missing_plates ?? []}
                            />
                        </React.Fragment>
                    ) : (
                        <Box
                            display='flex'
                            flexDirection='column'
                            height='100%'
                        >
                            <UploadIllusionEDLPullModSummary
                                editedPlates={editedPlates}
                                edlSummary={edlSummary}
                                getDuplicatePlates={getDuplicatePlates}
                                selectionModel={selectionModel}
                            />
                                <UploadIllusionModifyDatagrid
                                    edlSummary={edlSummary}
                                    editedPlates={editedPlates}
                                    getDuplicatePlates={getDuplicatePlates}
                                    pullType={pullType}
                                    rows={modifiedPlates}
                                    selectionModel={selectionModel}
                                    setRows={setModifiedPlates}
                                    setSelectionModel={setSelectionModel}
                                />
                        </Box>
                    )}
                </DialogContent>
                <Box sx={{ height: 'auto' }}>
                    <DialogActions>
                        <Button onClick={advancedSettings ? handleCancelAdvancedSettings : handleClose} sx={{ color: 'white' }}>
                            {illusionSubmissionStatus == 'success' ? 'Close' : 'Cancel'}
                        </Button>
                        {advancedSettings ? (
                            <Button
                                onClick={() => {
                                    setEditedPlates(modifiedPlates);
                                    setAdvancedSettings(false);
                                    enqueueSnackbar("Changes applied!", { variant: 'info' });
                                }}
                                sx={{ color: 'white' }}
                            >
                                Apply
                            </Button>
                        ) : (
                            <Button
                                disabled={submitDisabled}
                                onClick={handleSubmit}
                                sx={{ color: 'white' }}
                            >
                                Submit
                            </Button>
                        )}
                    </DialogActions>
                    <LinearProgress variant={illusionSubmissionStatus == 'progress' ? 'indeterminate' : 'determinate'}
                        value={illusionSubmissionStatus == 'progress' ? null : 100}
                        color={illusionSubmissionStatus == 'error' ? 'error' : illusionSubmissionStatus == 'success' ? 'success': 'secondary'}
                        sx={{ visibility: submitInProgress ? 'visible' : 'hidden' }}
                    />
                </Box>
            </Dialog>
            <EDLSubmissionWarningDialog
                open={EdlWarningDialogOpen}
                onClose={handleEdlWarningDialogClose}
            />
        </React.Fragment>
    );
}

export default UploadIllusionEDLDialog;
